import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";

import { DELETE_PURCHASE, DELETE_PURCHASE_RETURN, EDIT_PURCHASE } from "../../constants";
import { CardLayout, DeleteModal, MTable } from "../v3";
import { AnalyticsService, PurchaseService } from "../../services";
import { ProjectPurchaseTableColumns } from "../../pages/project/constants";
import { derivePdfColumns, useHasPermission } from "../../utils";
import { handlePdfDownload, Listing } from "../pdf";
import { VendorStatementTypes } from "../vendor/types";
import { usePersistedTableState } from "../../hooks/usePersistedTableState";

const DEFAULT_SORT = [{ field: "date", sort: "desc" as const }];

export const ProjectPurchase = () => {
  const [invoices, setInvoices] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  // Remembered per visit: Back restores these, forward navigation starts from the defaults.
  const {
    searchText,
    setSearchText,
    sortModel,
    setSortModel,
    dateRange,
    setDateRange,
    paginationModel: paginationState,
    setPaginationModel: setPaginationState,
  } = usePersistedTableState({
    tableId: "project-purchases",
    defaults: { sortModel: DEFAULT_SORT },
  });
  const [loading, setLoading] = useState(false);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, recordId: null, type: null });

  const hasEditPurchase = useHasPermission(EDIT_PURCHASE);
  const hasDeletePurchase = useHasPermission(DELETE_PURCHASE);
  const canDeletePurchaseReturn = useHasPermission(DELETE_PURCHASE_RETURN);

  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.user);

  const fetchData = useCallback(
    async (purchaseFilters) => {
      setLoading(true);

      try {
        const vendorStatement = await AnalyticsService.getVendorAnalytics({
          ...purchaseFilters,
          projectId: id,
          type: [VendorStatementTypes.purchase_invoice, VendorStatementTypes.purchase_return],
        });

        // setInvoices(vendorStatement?.data?.rows);
        setTotalCount(vendorStatement?.data?.count || 0);

        const vendorIds = Array.from(new Set(vendorStatement?.data?.rows?.map((item) => item.vendorId)));
        let vendors = [];
        if (vendorIds.length > 0) {
          const vendorResponse = await PurchaseService.getVendors({
            ids: vendorIds.join(","),
            limit: vendorIds.length,
            includeDeleted: true,
          });
          // setVendors(vendorResponse?.data?.rows);
          vendors = vendorResponse?.data?.rows || [];
        }

        const mappedInvoices = vendorStatement?.data?.rows.map((statement, index) => {
          const vendor = vendors.find((vendor) => vendor.id === statement.vendorId);
          return {
            id: statement.id,
            sl: index + 1 + ((purchaseFilters.page || 1) - 1) * (purchaseFilters.limit || 10),
            date: moment(statement.date).format("DD MMM YYYY"),
            vendorName: vendor?.name,
            amount: statement?.amount,
            vendorId: statement?.vendorId,
            type: statement.type === VendorStatementTypes.purchase_invoice ? "Purchase" : "Return",
          };
        });
        setInvoices(mappedInvoices);
      } catch (error) {
        toast.error("Failed to fetch purchase statement");
        console.error("🚀 ~ fetchData error:", error);
      } finally {
        setLoading(false);
      }
    },
    [id],
  );

  useEffect(() => {
    if (!dateRange) return; // wait for DateRangeInput default/apply callback

    fetchData({
      sortBy: sortModel[0]?.field,
      sortOrder: sortModel[0]?.sort,
      page: paginationState.page + 1,
      limit: paginationState.pageSize,
      fromDate: dateRange.startDate,
      toDate: dateRange.endDate,
    });
  }, [fetchData, sortModel, paginationState, id, dateRange]);

  const handleSearchChange = (searchValue) => {
    setSearchText(searchValue);
  };

  const handleDateRangeChange = (ranges) => {
    const selection = ranges?.[0];
    if (!selection?.startDate || !selection?.endDate) return;

    // WHY: Align purchase listing with other statements by filtering via picker-selected range.
    setDateRange({
      startDate: moment(selection.startDate).startOf("day").toISOString(),
      endDate: moment(selection.endDate).endOf("day").toISOString(),
    });
    setPaginationState((prev) => ({ ...prev, page: 0 }));
  };

  const handleDownloadPdfClick = async () => {
    setIsPdfGenerating(true);
    try {
      const pdfColumnHeaders = derivePdfColumns(ProjectPurchaseTableColumns());
      const printableRows = invoices.map((row) =>
        Object.fromEntries(
          Object.entries(row).map(([key, value]) => [key, value === undefined || value === null ? "" : String(value)]),
        ),
      );

      const pdfDocument = (
        <Listing title="Purchases" columnHeaders={pdfColumnHeaders} tableRows={printableRows} company={user?.company} />
      );

      await handlePdfDownload({
        document: pdfDocument,
        pdfTitle: "Purchases",
      });
    } catch (error) {
      toast.error("PDF generation failed");
    } finally {
      setIsPdfGenerating(false);
    }
  };

  const handlePaginationChange = (model) => {
    setPaginationState(model);
  };

  const onViewClick = (params, event) => {
    const { vendorId, id, type } = params.row;
    if (type === "Purchase") {
      navigate(`/vendor/${vendorId}/invoice/${id}`);
    } else {
      navigate(`/vendor/${vendorId}/return/${id}`);
    }
  };

  const openDeleteModal = (rowData) => {
    setDeleteModal({ isOpen: true, recordId: rowData.id, type: rowData.type });
  };

  const closeDeleteModal = () => {
    setDeleteModal({ isOpen: false, recordId: null, type: null });
  };

  const handleDelete = async () => {
    try {
      if (deleteModal.type === "Return") {
        await PurchaseService.deletePurchaseReturn(deleteModal.recordId);
      } else {
        await PurchaseService.deletePurchase(deleteModal.recordId);
      }
      toast.success("Record deleted successfully");
      fetchData({
        sortBy: sortModel[0]?.field,
        sortOrder: sortModel[0]?.sort,
        page: paginationState.page + 1,
        limit: paginationState.pageSize,
        fromDate: dateRange?.startDate,
        toDate: dateRange?.endDate,
      });
    } catch (error) {
      toast.error("Failed to delete record");
    } finally {
      closeDeleteModal();
    }
  };

  const handleActions = (rowData, actionType) => {
    switch (actionType) {
      case "edit":
        if (rowData.type === "Purchase") {
          navigate(`/vendor/purchase/edit/${rowData.id}?vendorId=${rowData.vendorId}`);
        }
        break;
      case "delete":
        openDeleteModal(rowData);
        break;
      default:
        return null;
    }
  };

  const columnHeaders = ProjectPurchaseTableColumns(handleActions, hasEditPurchase, hasDeletePurchase);

  return (
    <CardLayout>
      <MTable
        // Search data
        placeholder="Search Purchase"
        searchText={searchText}
        onSearchChange={handleSearchChange}
        // showSearchBar
        // Date range data
        showDateRangePicker
        dateRange={dateRange}
        onDateRangeChange={handleDateRangeChange}
        // Export data
        showExportButton
        isPdfGenerating={isPdfGenerating}
        onExportPdfClick={handleDownloadPdfClick}
        // Table data
        columnHeaders={columnHeaders}
        tableRows={invoices}
        rowCount={totalCount || 0}
        loading={loading}
        sortModel={sortModel}
        onSortChange={setSortModel}
        defaultSortModel={DEFAULT_SORT}
        paginationModel={paginationState}
        onPaginationChange={handlePaginationChange}
        onRowClick={onViewClick}
      />
      <DeleteModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        message={`Are you sure you want to delete this ${deleteModal.type === "Return" ? "return" : "purchase"}?`}
      />
    </CardLayout>
  );
};
